import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export interface InitialTypeBindingExpected {
  room_type_id: null;
  room_version: number;
  room_topology_revision: number;
  property_version: number;
  type_version: number;
}

export interface InitialRoomTypeBindingCommand {
  command_id: string;
  room_type_id: string;
  expected: InitialTypeBindingExpected;
  reason: string;
}

export interface InitialRoomTypeBindingReceipt {
  command_id: string;
  room_id: string;
  room_type_id: string;
  result_revision: number;
  effect: 'CHANGED';
  received_at: string;
}

type RecordValue = Record<string, unknown>;
const record = (value: unknown): value is RecordValue =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const positiveRevision = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) > 0 && Number(value) < 2 ** 63;
const exactKeys = (value: RecordValue, keys: readonly string[]) =>
  Object.keys(value).length === keys.length && keys.every((key) => key in value);
const expectedKeys = [
  'room_type_id',
  'room_version',
  'room_topology_revision',
  'property_version',
  'type_version',
] as const;
const receiptKeys = [
  'command_id',
  'room_id',
  'room_type_id',
  'result_revision',
  'effect',
  'received_at',
] as const;

function invalidResponse(status?: number): ApiError {
  return new ApiError({
    kind: 'invalid-response',
    service: 'core',
    status,
    message: '객실 유형 연결 응답을 확인하지 못했습니다.',
  });
}

export function validateInitialTypeBindingCommand(
  propertyId: string,
  roomId: string,
  command: InitialRoomTypeBindingCommand,
): void {
  const expected: unknown = record(command) ? command.expected : undefined;
  if (
    !record(command) ||
    !exactKeys(command, ['command_id', 'room_type_id', 'expected', 'reason']) ||
    !isUuid(propertyId) ||
    !isUuid(roomId) ||
    !isUuid(command.command_id) ||
    !isUuid(command.room_type_id) ||
    typeof command.reason !== 'string' ||
    command.reason.trim().length === 0 ||
    command.reason.length > 500 ||
    !record(expected) ||
    !exactKeys(expected, expectedKeys) ||
    expected.room_type_id !== null ||
    expectedKeys.slice(1).some((key) => !positiveRevision(expected[key]))
  )
    throw invalidResponse();
}

export function parseInitialTypeBindingReceipt(
  value: unknown,
  command: InitialRoomTypeBindingCommand,
  roomId: string,
): InitialRoomTypeBindingReceipt {
  const receipt = value;
  if (
    !record(receipt) ||
    !exactKeys(receipt, receiptKeys) ||
    !isUuid(receipt.command_id) ||
    receipt.command_id.toLowerCase() !== command.command_id.toLowerCase() ||
    !isUuid(receipt.room_id) ||
    receipt.room_id.toLowerCase() !== roomId.toLowerCase() ||
    !isUuid(receipt.room_type_id) ||
    receipt.room_type_id.toLowerCase() !== command.room_type_id.toLowerCase() ||
    !positiveRevision(receipt.result_revision) ||
    receipt.result_revision !== command.expected.room_topology_revision + 1 ||
    receipt.effect !== 'CHANGED' ||
    !isOffsetDateTime(receipt.received_at)
  )
    throw invalidResponse(200);
  return receipt as unknown as InitialRoomTypeBindingReceipt;
}

/** Send one explicit first-parent command; HTTP mutation replay policy remains disabled. */
export async function bindInitialRoomType(
  client: ServiceClient,
  propertyId: string,
  roomId: string,
  command: InitialRoomTypeBindingCommand,
): Promise<InitialRoomTypeBindingReceipt> {
  validateInitialTypeBindingCommand(propertyId, roomId, command);
  const value = await client.post<unknown>(
    `admin/properties/${propertyId}/rooms/${roomId}/initial-type-binding`,
    { json: command },
  );
  return parseInitialTypeBindingReceipt(value, command, roomId);
}
